import axiosConfig from "./axiosConfig";

// Système de cache pour les requêtes GET
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes

class AuteurService {
  constructor() {
    this.baseUrl = "/v1/authors";
    this.secretairesUrl = "/v1/secretaires";
  }

  /**
   * Vérifie si des données sont disponibles dans le cache
   * @param {string} cacheKey Clé de cache
   * @param {boolean} useCache Utiliser le cache ou non
   * @returns {Object|null} Données en cache ou null
   */
  _checkCache(cacheKey, useCache = true) {
    if (useCache && cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      if (Date.now() - cachedData.timestamp < CACHE_DURATION) {
        return cachedData.data;
      }
    }
    return null;
  }

  /**
   * Met en cache des données
   * @param {string} cacheKey Clé de cache
   * @param {Object} data Données à stocker
   * @param {boolean} useCache Activer la mise en cache
   */
  _setCache(cacheKey, data, useCache = true) {
    if (useCache) {
      cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Récupère tous les auteurs
   * @param {number} limit Nombre d'auteurs à récupérer
   * @param {boolean} useCache Utiliser le cache si disponible
   * @returns {Promise<Array>} Liste des auteurs
   */
  getAll(limit = 50, useCache = true) {
    const cacheKey = `authors-all-${limit}`;
    const cachedData = this._checkCache(cacheKey, useCache);
    
    if (cachedData) return Promise.resolve(cachedData);
    
    return axiosConfig.get(
      `${this.baseUrl}?limit=${limit}&order_by=id_aut&order_direction=desc`
    )
    .then(response => {
      const data = response.data.data || [];
      this._setCache(cacheKey, data, useCache);
      return data;
    })
    .catch(error => Promise.reject(error.response?.data || error.message));
  }

  /**
   * Récupère un auteur par son ID
   * @param {number} id ID de l'auteur
   * @param {boolean} useCache Utiliser le cache si disponible
   * @returns {Promise<Object>} L'auteur
   */
  getAuteurById(id, useCache = true) {
    const cacheKey = `author-${id}`;
    const cachedData = this._checkCache(cacheKey, useCache);
    
    if (cachedData) return Promise.resolve(cachedData);
    
    return axiosConfig.get(`${this.baseUrl}/${id}`)
      .then(response => {
        this._setCache(cacheKey, response.data, useCache);
        return response.data;
      })
      .catch(error => Promise.reject(error.response?.data || error.message));
  }

  /**
   * Crée un nouvel auteur
   * @param {Object|FormData} auteur Données de l'auteur
   * @returns {Promise<Object>} Auteur créé
   */
  createAuteurs(auteur) {
    return axiosConfig.post(this.baseUrl, auteur, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(response => {
      this.invalidateCache();
      return response.data;
    })
    .catch(error => Promise.reject(error.response?.data || error.message));
  }

  /**
   * Récupère tous les secrétaires
   * @param {number} limit Nombre de secrétaires à récupérer
   * @param {boolean} useCache Utiliser le cache si disponible
   * @returns {Promise<Array>} Liste des secrétaires
   */
  getAllSecretaire(limit = 50, useCache = true) {
    const cacheKey = `secretaires-all-${limit}`;
    const cachedData = this._checkCache(cacheKey, useCache);
    
    if (cachedData) return Promise.resolve(cachedData);
    
    return axiosConfig.get(
      `${this.secretairesUrl}?limit=${limit}&offset=0&order_by=id&order_direction=asc`
    )
    .then(response => {
      const data = response.data || [];
      this._setCache(cacheKey, data, useCache);
      return data;
    })
    .catch(error => Promise.reject(error.response?.data || error.message));
  }

  /**
   * Met à jour un auteur existant
   * @param {number} id ID de l'auteur
   * @param {Object|FormData} auteur Nouvelles données
   * @returns {Promise<Object>} Auteur mis à jour
   */
  updateAuteurs(id, auteur) {
    let formDataToSend;
    
    if (auteur instanceof FormData) {
      formDataToSend = auteur;
      formDataToSend.append("_method", "PUT");
    } else {
      formDataToSend = new FormData();
      formDataToSend.append("_method", "PUT");
      
      // Ajout des propriétés de l'objet auteur au FormData
      Object.entries(auteur).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
    }

    return axiosConfig.post(`${this.baseUrl}/${id}?_method=PUT`, formDataToSend, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(response => {
      this.invalidateCache();
      return response.data;
    })
    .catch(error => Promise.reject(error.response?.data || error.message));
  }

  /**
   * Supprime un auteur
   * @param {number} id ID de l'auteur
   * @returns {Promise<Object>} Résultat de la suppression
   */
  deleteAuteurs(id) {
    return axiosConfig.delete(`${this.baseUrl}/${id}`)
      .then(response => {
        this.invalidateCache();
        
        if (response.status >= 200 && response.status < 300) {
          return response.data;
        } else {
          return Promise.reject(response.data || { message: "Échec de la suppression" });
        }
      })
      .catch(error => {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          "Erreur inconnue";
          
        return Promise.reject({
          message: errorMessage,
          original: error,
        });
      });
  }

  /**
   * Crée plusieurs auteurs en parallèle
   * @param {Array<Object|FormData>} auteurs Liste des auteurs à créer
   * @returns {Promise<Array>} Résultats des créations
   */
  createMultipleAuteurs(auteurs) {
    const promises = auteurs.map(auteur => this.createAuteurs(auteur));
    return Promise.all(promises);
  }

  /**
   * Met à jour plusieurs auteurs en parallèle
   * @param {Array<Object>} updateData Tableau d'objets {id, auteur}
   * @returns {Promise<Array>} Résultats des mises à jour
   */
  updateMultipleAuteurs(updateData) {
    const promises = updateData.map(({ id, auteur }) => this.updateAuteurs(id, auteur));
    return Promise.all(promises);
  }

  /**
   * Invalide tout le cache des auteurs et secrétaires
   */
  invalidateCache() {
    for (const key of cache.keys()) {
      if (key.startsWith('author') || key.startsWith('secretaires')) {
        cache.delete(key);
      }
    }
  }

  /**
   * Invalide le cache pour un type spécifique
   * @param {string} cacheType Type de cache ('authors' ou 'secretaires')
   */
  invalidateCacheByType(cacheType) {
    for (const key of cache.keys()) {
      if (key.startsWith(cacheType)) {
        cache.delete(key);
      }
    }
  }
}

const AuteurServiceInstance = new AuteurService();
export default AuteurServiceInstance;